import { ArrowLeft } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export default function TermsOfUse() {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-gradient-to-b from-blue-50 via-white to-slate-50">
            {/* Header */}
            <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-border/50">
                <div className="px-6 py-4">
                    <div className="flex items-center gap-4 max-w-3xl mx-auto">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-2 -ml-2 hover:bg-secondary rounded-full transition-colors"
                        >
                            <ArrowLeft className="h-5 w-5" />
                        </button>
                        <h1 className="font-heading text-xl font-semibold">Terms of Use</h1>
                    </div>
                </div>
            </header>

            {/* Content */}
            <main className="px-6 py-8 max-w-3xl mx-auto">
                <div className="prose prose-slate prose-sm max-w-none">
                    <h1 className="text-2xl font-bold text-slate-900 mb-6">Terms of Use</h1>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">1. Agreement Overview</h2>
                        <p className="text-slate-600 mb-3">
                            These Terms of Use ("Terms") govern your access to and use of Voilaw.app and related applications, software, features, and services (collectively, the "Platform"), operated by Voilaw ("Voilaw," "we," "us," or "our").
                        </p>
                        <p className="text-slate-600 mb-3">
                            This is a binding legal agreement between you ("User," "you," or "your") and Voilaw. These Terms incorporate our <Link to="/privacy" className="text-[#1C6EE7] hover:underline">Privacy Policy</Link> and any additional policies or service-specific terms presented to you.
                        </p>
                        <p className="text-slate-600 mb-3">
                            By accessing or using the Platform, you represent that you are at least eighteen (18) years old and have the legal authority to enter into this Agreement. If you use the Platform on behalf of an organization, you represent that you are authorized to bind that organization, and "you" refers to that organization.
                        </p>
                        <p className="text-slate-600 font-medium">
                            If you do not agree to these Terms, do not access or use the Platform.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">2. SaaS Platform Only — No Legal Services</h2>
                        <p className="text-slate-600 mb-3">
                            Voilaw is a software-as-a-service (SaaS) platform that provides tools for time tracking, matter organization, workflow management, analytics, and related operational functions.
                        </p>
                        <p className="text-slate-600 mb-3 font-medium">
                            Voilaw is not a law firm and does not provide legal advice or legal services. Nothing on the Platform creates an attorney-client relationship between you and Voilaw.
                        </p>
                        <p className="text-slate-600 mb-2">You are solely responsible for:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li>How you use the Platform</li>
                            <li>The accuracy, legality, and appropriateness of any data you enter</li>
                            <li>Compliance with professional, ethical, and legal obligations applicable to you or your organization</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">3. License Grant</h2>
                        <p className="text-slate-600 mb-3">
                            Subject to your compliance with these Terms, Voilaw grants you a limited, non-exclusive, non-transferable, revocable license to access and use the Platform for your internal business or professional purposes.
                        </p>
                        <p className="text-slate-600">
                            All rights not expressly granted are reserved by Voilaw.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">4. Accounts and Security</h2>
                        <p className="text-slate-600 mb-3">
                            You are responsible for safeguarding your account credentials and all activity that occurs under your account. You agree to notify Voilaw immediately of any unauthorized access or use.
                        </p>
                        <p className="text-slate-600">
                            Voilaw may suspend or restrict access if it reasonably believes your account has been compromised or used in violation of these Terms.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">5. Subscriptions, Billing, and Payments</h2>
                        
                        <h3 className="text-base font-medium text-slate-700 mb-2">a. Subscription Model</h3>
                        <p className="text-slate-600 mb-3">
                            Certain features of the Platform require a paid subscription. Subscription tiers, pricing, usage limits, and features are described at the point of purchase or within the Platform.
                        </p>
                        
                        <h3 className="text-base font-medium text-slate-700 mb-2">b. Payment Processing (Stripe)</h3>
                        <p className="text-slate-600 mb-3">
                            Payments are processed by third-party payment processors, including Stripe. Voilaw does not store your full payment card information. By subscribing, you agree to Stripe's applicable terms and authorize Voilaw and Stripe to charge your selected payment method.
                        </p>
                        
                        <h3 className="text-base font-medium text-slate-700 mb-2">c. Auto-Renewal</h3>
                        <p className="text-slate-600 mb-3">
                            Subscriptions automatically renew at the end of each billing period unless canceled before renewal. You authorize recurring charges until cancellation.
                        </p>
                        
                        <h3 className="text-base font-medium text-slate-700 mb-2">d. Nonpayment and Enforcement</h3>
                        <p className="text-slate-600 mb-2">If payment fails or is disputed:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>Your subscription may be downgraded, suspended, or terminated</li>
                            <li>Access to paid features may be restricted</li>
                            <li>Data creation or exports may be limited until payment is resolved</li>
                        </ul>
                        <p className="text-slate-600 mb-3">
                            Voilaw may enforce plan limits, usage caps, and feature restrictions at any time.
                        </p>
                        
                        <h3 className="text-base font-medium text-slate-700 mb-2">e. No Refunds</h3>
                        <p className="text-slate-600">
                            Except where required by law or expressly stated in writing, fees are non-refundable.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">6. Complimentary, Trial, and Promotional Access</h2>
                        <p className="text-slate-600 mb-2">
                            Voilaw may, at its discretion, offer free trials, complimentary access, or promotional subscriptions. Such access:
                        </p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>Is time-limited</li>
                            <li>Automatically expires unless converted to a paid plan</li>
                            <li>May impose feature or usage limits</li>
                        </ul>
                        <p className="text-slate-600">
                            Upon expiration, access may be restricted without deletion of existing data, subject to plan rules.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">7. User Content and Data</h2>
                        <p className="text-slate-600 mb-3">
                            You retain ownership of content and data you submit to the Platform ("User Content"). You grant Voilaw a limited license to host, process, and display User Content solely to operate and improve the Platform.
                        </p>
                        <p className="text-slate-600">
                            You represent that you have all necessary rights to submit such content and that it does not violate applicable law or third-party rights.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">8. Confidentiality and Data Handling</h2>
                        <p className="text-slate-600 mb-2">
                            Voilaw will treat User Content and account data as confidential, subject to:
                        </p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>Our <Link to="/privacy" className="text-[#1C6EE7] hover:underline">Privacy Policy</Link></li>
                            <li>Lawful requests or legal obligations</li>
                            <li>Aggregated, anonymized analytics used to improve the Platform</li>
                        </ul>
                        <p className="text-slate-600">
                            You are responsible for ensuring that your use of the Platform complies with confidentiality obligations owed to third parties.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">9. Prohibited Conduct</h2>
                        <p className="text-slate-600 mb-2">You may not:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>Reverse engineer or copy the Platform</li>
                            <li>Circumvent security or usage limits</li>
                            <li>Access non-public systems or data</li>
                            <li>Upload malicious code</li>
                            <li>Misuse the Platform in violation of law or professional rules</li>
                        </ul>
                        <p className="text-slate-600">
                            Voilaw may investigate and take appropriate action, including suspension or termination.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">10. SMS and Electronic Communications Consent</h2>
                        <p className="text-slate-600 mb-2">
                            By providing a phone number, you consent to receive transactional SMS or MMS messages from Voilaw or its service providers (e.g., account alerts, security notices, billing reminders).
                        </p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>Message frequency may vary</li>
                            <li>Message and data rates may apply</li>
                            <li>You may opt out at any time by replying STOP</li>
                        </ul>
                        <p className="text-slate-600">
                            Voilaw does not send marketing texts without separate consent and does not sell phone numbers.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">11. Third-Party Services</h2>
                        <p className="text-slate-600">
                            The Platform may integrate with third-party services. Voilaw is not responsible for third-party tools, content, or availability. Your use of third-party services is governed by their terms.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">12. Disclaimers</h2>
                        <p className="text-slate-600 mb-3 font-medium uppercase text-xs">
                            THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE." VOILAW DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
                        </p>
                        <p className="text-slate-600">
                            Voilaw does not guarantee uninterrupted, error-free, or loss-free operation.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">13. Limitation of Liability</h2>
                        <p className="text-slate-600 mb-2 font-medium uppercase text-xs">TO THE MAXIMUM EXTENT PERMITTED BY LAW:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li className="uppercase text-xs">VOILAW SHALL NOT BE LIABLE FOR INDIRECT, INCIDENTAL, CONSEQUENTIAL, OR SPECIAL DAMAGES</li>
                            <li className="uppercase text-xs">VOILAW'S TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU TO VOILAW IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">14. Indemnification</h2>
                        <p className="text-slate-600 mb-2">
                            You agree to indemnify and hold harmless Voilaw and its affiliates from claims arising out of:
                        </p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li>Your use of the Platform</li>
                            <li>Your violation of these Terms</li>
                            <li>Your User Content</li>
                        </ul>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">15. Termination</h2>
                        <p className="text-slate-600 mb-2">Voilaw may suspend or terminate access:</p>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1 mb-3">
                            <li>For violation of these Terms</li>
                            <li>For nonpayment</li>
                            <li>For legal or security reasons</li>
                        </ul>
                        <p className="text-slate-600">
                            You may cancel your subscription at any time through your account settings. Termination does not relieve payment obligations incurred before cancellation.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">16. Arbitration and Class Action Waiver</h2>
                        <p className="text-slate-600 mb-3">
                            All disputes shall be resolved by binding arbitration in Nassau County, New York, under the rules of the American Arbitration Association.
                        </p>
                        <p className="text-slate-600 font-medium">
                            You waive the right to participate in class or representative actions.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">17. Governing Law</h2>
                        <p className="text-slate-600">
                            These Terms are governed by the laws of the State of New York, without regard to conflict-of-law principles.
                        </p>
                    </section>

                    <section className="mb-8">
                        <h2 className="text-lg font-semibold text-slate-800 mb-3">18. Miscellaneous</h2>
                        <ul className="list-disc pl-6 text-slate-600 space-y-1">
                            <li><strong>Entire Agreement:</strong> These Terms constitute the entire agreement between you and Voilaw</li>
                            <li><strong>No Assignment:</strong> You may not assign without consent</li>
                            <li><strong>Severability:</strong> Invalid provisions do not affect the remainder</li>
                            <li><strong>No Waiver:</strong> Failure to enforce is not a waiver</li>
                        </ul>
                    </section>

                    <div className="mt-12 pt-6 border-t border-slate-200">
                        <p className="text-xs text-slate-400 text-center mb-2">
                            Last updated: January 2026
                        </p>
                        <p className="text-xs text-slate-400 text-center">
                            <Link to="/privacy" className="text-slate-500 hover:text-[#1C6EE7] hover:underline">
                                Privacy Policy
                            </Link>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}
